import { AspectRatioPicture } from "@faire/retailer-visitor-shared/components/Image/AspectRatioPicture";
import { EAEAEA_PIXEL } from "@faire/retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { generateProductLinkPropsForLoggedOutRetailer } from "@faire/retailer-visitor-shared/lib/generateProductLinkPropsForLoggedOutRetailer";
import {
  optimizeImageUrlFillRectangle,
  optimizeImageUrlFillSquare,
} from "@faire/web--source/common/images/optimizeImages";
import {
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { TransparencyOverlay } from "@faire/web--source/ui/TransparencyOverlay";
import { IBrandReviewTile } from "@faire/web-api--source/indigofair/data/IBrandReviewTile";
import React from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

const DOUBLE_IMAGE_RATIO = 3 / 4;
const SINGLE_IMAGE_RATIO = 3 / 2;
const TRIPLE_IMAGE_RATIO = 1;
const IMAGE_HEIGHT = 241;

const getAltTag = (
  productName: string,
  brandName: string,
  strictLocalize: StrictLocalizeFunction
) => {
  return strictLocalize(
    {
      defaultMessage:
        "Sign up to buy wholesale {ProductName} by {brandCompanyName}.",
      description: {
        text: "product tile on suppliers page. click on the tile will open the signup modal",
        img: "https://cdn.faire.com/fastly/2a25db1792fa2306b53754c3e5925ba1a6a3c0d865e5fdad326a92999a61fc0f.png",
      },
    },
    { ProductName: productName, brandCompanyName: brandName }
  );
};

interface IProps {
  onProductClick: (productToken: string) => void;
  brandToken: string | undefined;
  products: Array<IBrandReviewTile.IProductData>;
  brandName: string | undefined;
}

export const BrandReviewTileImages = ({
  brandToken,
  products,
  onProductClick,
  brandName,
}: IProps) => {
  const location = useLocation();
  const { strictLocalize } = useStrictLocalization();
  const isSSR = useIsSSR();

  if (!products) {
    return null;
  }

  switch (products.length) {
    case 1:
    case 2:
      return (
        <SingleAndDoubleImageGrid>
          {products.map((product, index) => {
            const productLinkProps =
              generateProductLinkPropsForLoggedOutRetailer({
                productToken: product.product_token,
                brandToken,
                location,
              });

            return (
              <SingleDoubleImageWrapper
                $length={products.length}
                key={`${product.product_token}-${index}`}
              >
                <Link
                  to={productLinkProps?.productUrl ?? ""}
                  replace={productLinkProps?.replace}
                  onClick={() => onProductClick(product.product_token ?? "")}
                >
                  <FullSizeAspectPicture
                    $ratio={
                      products.length === 1
                        ? SINGLE_IMAGE_RATIO
                        : DOUBLE_IMAGE_RATIO
                    }
                    $width="100%"
                    $height="100%"
                    $hideBorder
                  >
                    <FullSizeImage
                      alt={getAltTag(
                        product.product_name ?? "",
                        brandName ?? "",
                        strictLocalize
                      )}
                      src={
                        product.image
                          ? optimizeImageUrlFillRectangle(
                              product.image,
                              isSSR,
                              products.length === 1
                                ? IMAGE_HEIGHT * SINGLE_IMAGE_RATIO
                                : IMAGE_HEIGHT * DOUBLE_IMAGE_RATIO,
                              IMAGE_HEIGHT
                            )
                          : EAEAEA_PIXEL
                      }
                    />
                    <TransparencyOverlay />
                  </FullSizeAspectPicture>
                </Link>
              </SingleDoubleImageWrapper>
            );
          })}
        </SingleAndDoubleImageGrid>
      );
    default:
      return (
        <TripleImageGrid>
          {products.slice(0, 3).map((product, index) => {
            const productLinkProps =
              generateProductLinkPropsForLoggedOutRetailer({
                productToken: product.product_token,
                brandToken,
                location,
              });
            return (
              <TripleImageWrapper
                data-test-id={`image-wrapper-${index}`}
                key={`${product.product_token}-${index}`}
                $index={index}
              >
                <Link
                  to={productLinkProps?.productUrl ?? ""}
                  replace={productLinkProps?.replace}
                  onClick={() => onProductClick(product.product_token ?? "")}
                >
                  <FullSizeAspectPicture
                    $ratio={TRIPLE_IMAGE_RATIO}
                    $height="100%"
                    $hideBorder
                  >
                    <FullSizeImage
                      alt={getAltTag(
                        product.product_name ?? "",
                        brandName ?? "",
                        strictLocalize
                      )}
                      src={
                        product.image
                          ? optimizeImageUrlFillSquare(
                              product.image,
                              isSSR,
                              IMAGE_HEIGHT
                            )
                          : EAEAEA_PIXEL
                      }
                    />
                    <TransparencyOverlay />
                  </FullSizeAspectPicture>
                </Link>
              </TripleImageWrapper>
            );
          })}
        </TripleImageGrid>
      );
  }
};

const TripleImageGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 1fr 1fr;
  grid-gap: 2px;
`;

const TripleImageWrapper = styled.div<{
  $index: number;
}>`
  ${({ $index }) => ($index === 0 ? "grid-row: 1 / span 2;" : "")}
`;

const SingleAndDoubleImageGrid = styled.div`
  height: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr;
  grid-gap: 2px;
`;

const SingleDoubleImageWrapper = styled.div<{
  $length: number;
}>`
  ${({ $length }) => ($length === 1 ? "grid-column: 1 / span 2;" : "")}
`;

const FullSizeAspectPicture = styled(AspectRatioPicture)`
  width: 100%;
  height: 100%;
`;

const FullSizeImage = styled.img`
  width: 100%;
  height: 100%;
`;
